import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, Package, Loader2, CheckCheck } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface Notification {
  id: number;
  userId: number;
  type: string;
  title: string;
  content: string;
  channel: string;
  waSent: number;
  isRead: number;
  createdAt: number;
}

function TypeIcon({ type }: { type: string }) {
  if (type === 'low_stock') {
    return <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />;
  }
  if (type === 'order_digest') {
    return <Package className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />;
  }
  return <Bell className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />;
}

export default function AdminNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const handleUnauthorized = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/notifications');
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || '載入失敗，請重試');
        return;
      }
      setNotifications(data as Notification[]);
    } catch {
      setError('網絡錯誤，請重試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id: number) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: 1 } : n)));
    try {
      const res = await apiFetch(`/api/public/notifications/${id}/read`, { method: 'POST' });
      if (res.status === 401) {
        handleUnauthorized();
      }
    } catch {
      // 本地已標記已讀，忽略網絡錯誤
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      const res = await apiFetch('/api/public/notifications/read-all', { method: 'POST' });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || '操作失敗，請重試');
        return;
      }
      setNotifications(prev => prev.map(n => ({ ...n, isRead: 1 })));
    } catch {
      setError('網絡錯誤，請重試');
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => n.isRead === 0).length;

  return (
    <AdminLayout currentPage="notifications" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Bell className="w-6 h-6 text-orange-600" />
              通知中心
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              系統通知（低庫存警告、每日訂單摘要等）
            </p>
          </div>
          <button
            onClick={markAllRead}
            disabled={unreadCount === 0 || markingAll}
            className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 self-start sm:self-auto"
          >
            {markingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
            全部標記已讀
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            載入中...
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Bell className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">暫無通知</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(n => {
              const unread = n.isRead === 0;
              return (
                <div
                  key={n.id}
                  onClick={() => {
                    if (unread) markRead(n.id);
                  }}
                  className={
                    unread
                      ? 'rounded-lg border border-gray-200 dark:border-gray-700 border-l-4 border-l-orange-500 bg-orange-50/50 dark:bg-orange-900/10 p-4 cursor-pointer hover:shadow-sm transition-shadow'
                      : 'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4'
                  }
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <TypeIcon type={n.type} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <h3 className="font-semibold text-gray-800 dark:text-white">{n.title}</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(n.createdAt * 1000).toLocaleString('zh-HK')}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {n.content}
                      </p>
                      <div className="mt-2 text-right">
                        {n.waSent === 1 ? (
                          <span className="text-xs text-green-600 dark:text-green-400">✓ 已發 WhatsApp</span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">僅面板通知</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
